import React, { useCallback, useState } from 'react'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import _each from 'lodash.foreach'
import _map from 'lodash.map'
import { useFormik } from 'formik'

import { PrimaryButton } from '../../styled/button'
import OptionsContainer from '../../styled/options-container'
import getSongFirstChord from '../../utils/song-first-chord'
import durationFromSongs from '../../utils/duration-from-songs'
import computeNumbering from '../../utils/compute-numbering'
import { generateSetlistPdf } from '../../utils/setlist-pdf'

import styled from 'styled-components'

const CheckLabel = styled.div`
  display: inline-block;
  label {
    display: inline-block;
    padding-right: 15px;
    white-space: nowrap;
  }
  input {
    vertical-align: middle;
    margin-right: 5px;
  }
  label span {
    vertical-align: middle;
  }
`

const Options = styled.div`
  display: inline-block;
  margin-left: 15px;
`

export const options = [
  { label: 'No.', name: 'number', value: true, getFn: (idx) => `${idx}. ` },
  { label: 'Title', name: 'title', value: true, getFn: (idx, song) => `${song.title}\t` },
  { label: 'Artist', name: 'artist', value: false, getFn: (idx, song) => `${song.artist}\t` },
  { label: 'Key', name: 'key', value: false, getFn: (idx, song) => `${song.key ? song.key : '...'}\t` },
  { label: 'Tempo', name: 'tempo', value: false, getFn: (idx, song) => `${song.tempo ? song.tempo : '...'}\t` },
  { label: 'Duration', name: 'duration', value: false, getFn: (idx, song) => `${song.duration ? song.duration : '...'}\t` },
  { label: 'First Chord', name: 'firstChord', value: false, getFn: (idx, song) => `${getSongFirstChord(song)}\t` },
  { label: 'Meta 1', name: 'meta1', value: false, getFn: (idx, song) => `${song.meta1 ? song.meta1 : '...'}\t` },
  { label: 'Meta 2', name: 'meta2', value: false, getFn: (idx, song) => `${song.meta2 ? song.meta2 : '...'}\t` },
  { label: 'Meta 3', name: 'meta3', value: false, getFn: (idx, song) => `${song.meta3 ? song.meta3 : '...'}\t` },
  { label: 'Meta 4', name: 'meta4', value: false, getFn: (idx, song) => `${song.meta4 ? song.meta4 : '...'}\t` }
]

const getInitStateOptions = (optArr) => {
  const columns = {}
  _each(optArr, opt => {
    columns[opt.name] = opt.value
  })
  return columns
}

const getEntries = (setlist) => {
  if (setlist.entries && setlist.entries.length > 0) {
    return setlist.entries.map(e => {
      if (e.song) {
        const songId = e.song._id || e.song
        const songObj = (setlist.songs || []).find(s => (s._id || s) === songId)
        return songObj || { _id: songId }
      }
      return e
    })
  }
  return setlist.songs || []
}

export const buildClipBoard = (setlist, columns) => {
  let txt = `${setlist.title}\n\n`
  const entries = getEntries(setlist)
  const numbered = computeNumbering(entries)

  _each(numbered, ({ entry, songNumber }) => {
    if (entry.type === 'note') {
      if (entry.isBreak) {
        txt = `${txt}\n--- ${entry.text || 'Set Break'} ---\n\n`
      } else {
        txt = `${txt}[Note: ${entry.text}]\n`
      }
      return
    }

    // Song entry
    _each(options, (opt) => {
      if (columns[opt.name] === true) {
        txt = `${txt}${opt.getFn(songNumber, entry)}`
      }
    })
    txt = `${txt}\n`
  })

  // Duration only counts songs
  const songEntries = entries.filter(e => e.type !== 'note')
  const { missing, totalFormated } = durationFromSongs(songEntries)

  if (columns.duration) {
    txt = `${txt} Duration: ${totalFormated}`
    if (missing !== 0) {
      txt = `${txt}, ${missing} missing duration`
    }
  }

  return txt
}

const CopySetList = ({ setlist }) => {
  const [copied, setCopied] = useState(false)

  const { values, handleChange, setFieldValue } = useFormik({
    initialValues: getInitStateOptions(options)
  })

  const onCopy = useCallback(() => {
    setCopied(true)
    setTimeout(() => {
      setCopied(false)
    }, 2000)
  })

  const copyText = buildClipBoard(setlist, values)
  return (
    <>
      <OptionsContainer>
        <CopyToClipboard text={copyText} onCopy={onCopy}>
          <PrimaryButton>Copy to Clipboard</PrimaryButton>
        </CopyToClipboard>
        <PrimaryButton onClick={() => generateSetlistPdf(setlist, values)} style={{ marginLeft: '10px' }}>
          Save as PDF
        </PrimaryButton>
        <Options>
          {_map(options, (opt, idx) => (
            <CheckLabel key={`copy-setlist-option=${idx}`} onClick={() => setFieldValue(opt.name, !values[opt.name])}>
              <label htmlFor={opt.name}>
                <input type='checkbox' name={opt.name} onChange={handleChange} checked={values[opt.name]} /><span>{opt.label}</span>
              </label>
            </CheckLabel>
          ))}
        </Options>
        {copied && <span>Copied!</span>}
      </OptionsContainer>
    </>
  )
}

export default CopySetList
